import * as React from 'react';
import {RouteComponentProps, withRouter} from "react-router";
import LeadQuoteFilesAttachedEvent, {LeadQuoteFilesAttachedEventData} from "../../../model/lead/event/LeadQuoteFilesAttachedEvent";
import {LeadEventTypeEnum} from "../../../model/lead/event/LeadEvent";
import LeadQuoteStatusChangedEvent from "../../../model/lead/event/LeadQuoteStatusChangedEvent";
import {RootState} from "../../../store/reducer/root/RootState";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import Toast from "../../helper/Toast";
import {acceptQuote} from "../../../store/action/lead/lead";
import ConfirmQuoteAcceptModal from "../modal/ConfirmQuoteAcceptModal";
import CommonStreamItem, {StreamItemAction} from "../../../hiberUiKit/src/molecules/StreamItem/CommonStreamItem";
import LeadQuoteCommentCreatedEvent from "../../../model/lead/event/LeadQuoteCommentCreatedEvent";
import QuoteEmailModal from "../../lead/quote/QuoteEmailModal";
import {getIndividualContact} from "../../../store/view/contact";
import Routes from "../../../navigation/route/Route";
import QuoteDetailsModal from "../../lead/quote/QuoteDetailsModal";

interface ComponentProps {
	/**
	 * Lead activity to display.
	 */
	activity: LeadQuoteFilesAttachedEvent | LeadQuoteCommentCreatedEvent | LeadQuoteStatusChangedEvent;
	/**
	 * Indicates if action are disabled
	 */
	readOnly?: boolean
}

interface ReduxProps {
	/**
	 * Indicates if activity quote is accepted.
	 */
	isQuoteAccepted: boolean;
	/**
	 * Indicates if quote lead contact has email address.
	 */
	hasContactAnEmail: boolean;
	/**
	 * Indicates If exist newer version of this quote
	 */
	existsNewerVersion: boolean
}

interface DispatchProps {
	/**
	 * Accept quote action.
	 * @param {string} leadId
	 * @param {string} quoteId
	 */
	acceptQuote: (leadId: string, quoteId: string) => void;
}

interface State {
	/**
	 * Indicates if quote accept confirmation modal is visible.
	 */
	confirmationVisible: boolean;
	/**
	 * Indicates if email modal is visible.
	 */
	emailModalVisible: boolean;
	/**
	 * Indicate if quote details modal is visible.
	 */
	detailsVisible: boolean;
}

type Props = ComponentProps & RouteComponentProps<{}> & ReduxProps & DispatchProps;

class LeadActivityQuoteItemWithAction extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {confirmationVisible: false, emailModalVisible: false, detailsVisible: false};
		this.onModalConfirm = this.onModalConfirm.bind(this);
	}

	render() {
		let actions: StreamItemAction[] = [
			StreamItemAction.new("Show details", () => this.setState({detailsVisible: true})),
			StreamItemAction.new("Review Quote", this.redirectToQuoteReview),
		];
		if (!this.props.isQuoteAccepted && !this.props.readOnly && !this.props.existsNewerVersion) {
			actions.push(StreamItemAction.new("Accept quote", () => this.setState({confirmationVisible: true})));
		}
		if (this.props.hasContactAnEmail && !this.props.existsNewerVersion) {
			actions.push(StreamItemAction.new("Send email", () => this.setState({emailModalVisible: true})));
		}
		const quoteId = (this.props.activity.data as LeadQuoteFilesAttachedEventData).quoteId;
		return (
			<React.Fragment>
				<CommonStreamItem event={this.props.activity} actions={actions}/>
				{this.state.confirmationVisible &&
				<ConfirmQuoteAcceptModal onClose={() => this.setState({confirmationVisible: false})}
										 onConfirm={this.onModalConfirm}/>}
				{this.state.emailModalVisible &&
				<QuoteEmailModal quoteId={quoteId}
								 onClose={() => this.setState({emailModalVisible: false})}/>}
				{this.state.detailsVisible &&
				<QuoteDetailsModal onClose={() => this.setState({detailsVisible: false})}
								   quoteId={quoteId}
								   onReviewQuoteClick={this.redirectToQuoteReview}
								   versionNumber={this.props.activity.data.versionNumber}/>}
			</React.Fragment>
		);
	}

	onModalConfirm() {
		this.setState({confirmationVisible: false}, () =>
			this.props.acceptQuote(this.props.activity.leadId, this.props.activity.data.quoteId));
	}

	redirectToQuoteReview = () => this.props.history.push(
		this.props.existsNewerVersion ?
			Routes.LeadQuoteHistoricalView(this.props.activity.referenceNumber, this.props.activity.data.quoteId,
				this.props.activity.data.versionNumber) :
			Routes.LeadQuoteView(this.props.activity.referenceNumber, this.props.activity.data.quoteId));
}

function mapStateToProps(state: RootState, props: ComponentProps) {

	const lead = state.lead.leadsMap[props.activity.leadId];
	const hasContactAnEmail = getIndividualContact(lead.data.principalContactId, state)
		.map(ctx => ctx.hasEmailAddress()).getOrElse(false);
	const isQuoteAccepted = state.lead.events.getOrElse([]).some(e => {
		const quoteStatusEvent = e as LeadQuoteStatusChangedEvent;
		return e.type === LeadEventTypeEnum.QuoteStatusChanged
			&& quoteStatusEvent.data.quoteId === props.activity.data.quoteId
			&& quoteStatusEvent.data.status === "ACCEPTED"
	});
	const existsNewerVersion = state.lead.events.getOrElse([]).some(
		e => e.type == LeadEventTypeEnum.QuoteFilesAttached && (e as LeadQuoteFilesAttachedEvent).data.quoteId ==
			props.activity.data.quoteId && (e as LeadQuoteFilesAttachedEvent).data.versionNumber >
			props.activity.data.versionNumber)
	return ({isQuoteAccepted, hasContactAnEmail, existsNewerVersion})
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		acceptQuote: (leadId: string, quoteId: string) =>
			dispatch(acceptQuote(leadId,
				quoteId,
				() => Toast.success("Quote has been accepted", "Quote accept"),
				(error: any) => {
					let err: string;
					if (error && error.response && error.response.status === 412)
						err = error.response.data.message
					else
						err = "Sorry, something went wrong. Try again later.";
					Toast.error(err, "Quote accept");
				}))
	};
}

export default withRouter<ComponentProps>(connect(mapStateToProps, mapDispatchToProps)(LeadActivityQuoteItemWithAction))
